import React from "react";
import { StatusWrapper, StatusText, SvgWrapper, TrackCircle, ProgressCircle } from "./PieChart.elements";

const statusMapping = {
  // "Uploaded" from the user's POV starts as soon as the backend finishes assembly. Anything
  // upstream of READY_TO_UPLOAD_TO_PROVIDER (chunked-upload states) is still "Processing";
  // anything downstream is bookkeeping the user doesn't need to distinguish from "Uploaded"
  // until the file is actually Edited/Tagged/Published.
  Uploaded: ["READY_TO_UPLOAD_TO_PROVIDER", "IN_PROGRESS_UPLOAD_TO_PROVIDER", "READY_FOR_CAT_DV", "CAT_DV_INGESTED", "UPLOADED"],
  Processing: ["PENDING_CHUNKS", "CHUNK_UPLOAD_IN_PROGRESS", "CHUNK_UPLOAD_ASSEMBLING"],
  Paused: ["PAUSED"],
  Cancelled: ["CANCELLED"],
  // CatDV moves a package into the *_IN_PROGRESS states while the work is happening on their
  // side; without these the ring fell through to "Unknown" for the whole edit/tag window.
  Editing: ["EDIT_IN_PROGRESS"],
  Edited: ["EDITED"],
  Tagging: ["TAGGING_IN_PROGRESS"],
  Tagged: ["TAGGED"],
  // Processing is done; Forge is holding the package until its embargo passes, then
  // distributes it. Distinct from "On Hold", which means the newsroom is waiting on
  // something from the contributor — here nobody has to do anything but wait for a date.
  Embargoed: ["WAITING_FOR_EMBARGO"],
  Distributed: ["PUBLISHED"],
  Rejected: ["REJECTED"],
  // Forge paused the package mid-pipeline. Its own bucket rather than folded into
  // "Uploaded": a hold can last days, and the contributor should see that the stall is
  // deliberate instead of watching a package sit in a stage that looks stuck.
  "On Hold": ["ON_HOLD"],
};

const mapBackendStatusToFrontend = (backendStatus) => {
  // Map backend status to frontend status
  for (const [frontendStatus, backendStatuses] of Object.entries(
    statusMapping
  )) {
    if (backendStatuses.includes(backendStatus)) {
      return frontendStatus;
    }
  }
  return "Unknown"; // Default if status doesn't match any mapped status
};

const StatusPieChart = ({ status }) => {
  const frontendStatus = mapBackendStatusToFrontend(status);
  // Map status to color and percentage
  const statusMap = {
    Processing: {color: "#8095B7", percentage: 12},
    Paused: { color: "#F5C754", percentage: 12 },
    Cancelled: { color: "#D9D9D9", percentage: 100 },
    Uploaded: { color: "#3E79BD", percentage: 25 },
    // In-progress states get hues of their own — teal and orchid are the two gaps left in this
    // palette, and both clear the colourblind-separation floor against every colour above
    // (worst pair: Editing vs Processing, protan dE 8.6). They sit at the ring midpoint between
    // the step before and the step they lead into, so the chart still reads as forward motion.
    Editing: { color: "#2EC4B6", percentage: 37 },
    Edited: { color: "#F5C754", percentage: 50 },
    Tagging: { color: "#D580FF", percentage: 62 },
    Tagged: { color: "#F5A222", percentage: 75 },
    // Indigo, not the amber of Paused / On Hold: those mean someone still has to act
    // (the contributor owes information), while an embargo clears itself on its date.
    // Indigo is the last gap in this palette and stays separable from the blue of
    // Uploaded and the orchid of Tagging. The ring sits between Tagged and Distributed:
    // the work is finished, only the date is left.
    Embargoed: { color: "#6C5CE7", percentage: 88 },
    Distributed: { color: "#69BD44", percentage: 100 },
    Unknown: { color: "#D9D9D9", percentage: 0 },
    Rejected: { color: "#F15E6B", percentage: 100 },
    // Same amber as Paused — both mean "deliberately stopped", just at different phases,
    // so reusing the hue keeps that reading consistent without adding a colour to a
    // palette whose separation was worked out above.
    //
    // The ring sits mid-way because it has to: Forge sends only the status, not the stage
    // the package was held from, so the Dashboard genuinely cannot know how far along it
    // was. Treat this as "somewhere in processing", not a measurement.
    "On Hold": { color: "#F5C754", percentage: 50 },
  };

  const { color, percentage } = statusMap[frontendStatus];

  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
      <StatusWrapper>
        <SvgWrapper viewBox="0 0 40 40">
          <TrackCircle
              cx="20"
              cy="20"
              r={radius}
          />
          <ProgressCircle
              cx="20"
              cy="20"
              r={radius}
              stroke={color}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 20 20)"
          />
        </SvgWrapper>
        <StatusText>{frontendStatus}</StatusText>
      </StatusWrapper>
  );
};

export default StatusPieChart;
